import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'next-i18next';

// components
import Button from '@components/_atoms/Button';
import TextInputBox from '@components/_molecules/TextInputBox';
import { Toast } from '@components/_atoms/Toast';

// constants
import { ButtonColor, ButtonType, TextInputVariant } from '@constants/atoms';
import { MOBILE_MODE } from '@constants/size';

// hooks
import { usePatchCompanyNameFetch } from '@hooks/fetch/useUserFetch';
import DefaultButtonBox from '@components/_molecules/DefaultButtonBox';
import ButtonCode from '@constants/log/buttonCode';

const Layout = styled.form``;

const ButtonBoxWrapper = styled.div`
  margin-top: 80px;

  @media ${MOBILE_MODE} {
    margin-top: 246px;
  }
`;

export default function CompanyNameEditForm() {
  const { t } = useTranslation('user');
  const router = useRouter();
  const queryClient = useQueryClient();

  const [companyName, setCompanyName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const disabled = !companyName || !!errorMessage;

  const { patchCompanyNameFetchMutate } = usePatchCompanyNameFetch();

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCompanyName(value);

    checkValidation(value);
  };

  const checkValidation = (value: string) => {
    let message = '';

    if (!value) message = t('edit.require-error-message');
    setErrorMessage(message);
  };

  const handleFormSubmit = () => {
    patchCompanyNameFetchMutate(
      { company_name: companyName },
      {
        onSuccess: () => {
          router.back();
          Toast.success(t('edit.success-message'));
          queryClient.invalidateQueries(['user']);
        },
      },
    );
  };

  return (
    <Layout
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit();
      }}>
      <TextInputBox
        title={t('edit.companyName-title')}
        textInput={{
          variant: errorMessage ? TextInputVariant.ERROR : TextInputVariant.DEFAULT,
          value: companyName,
          onChange: handleFormChange,
          placeholder: t('edit.companyName-placeholder'),
        }}
        errorMessage={errorMessage}
        isRequired
      />
      <ButtonBoxWrapper>
        <DefaultButtonBox
          button1={
            <Button
              text={t('edit.back-button')}
              type={ButtonType.Button}
              color={ButtonColor.OutlinedBlack}
              onClick={() => router.back()}
              steelbosoId={ButtonCode.BTN_BACK}
            />
          }
          button2={<Button disabled={disabled} text={t('edit.submit-button')} steelbosoId={ButtonCode.BTN_SUBMIT} onClick={handleFormSubmit} />}
        />
      </ButtonBoxWrapper>
    </Layout>
  );
}
